import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
} from "@mui/material";

import planeIcon from "assets/analytics-plane-icon.svg";
import locationIcon from "assets/analytics-location-icon.svg";
import carIcon from "assets/analytics-car-green-icon.svg";

const airlinesData = [
  { name: "Airpeace", bookings: 186, revenue: "₦2240K" },
  { name: "Arik Air", bookings: 186, revenue: "₦2240K" },
  { name: "Dana Air", bookings: 186, revenue: "₦2240K" },
  { name: "Azman Air", bookings: 186, revenue: "₦2240K" },
  { name: "MAX Air", bookings: 186, revenue: "₦2240K" },
];

const destinationsData = [
  { name: "Victoria Island", trips: 89, revenue: "₦2240K" },
  { name: "Ikeja", trips: 76, revenue: "₦2240K" },
  { name: "Lekki", trips: 68, revenue: "₦2240K" },
  { name: "Kubwa", trips: 50, revenue: "₦2240K" },
  { name: "Area 10", trips: 48, revenue: "₦2240K" },
];

export default function TopPerformingServices({
  airlines,
  flightDestinations,
  rideDestinations,
}: {
  airlines?: Array<{ bookings: number; revenue: number; airline: string }>;
  flightDestinations?: Array<{
    trips: number;
    revenue: number;
    destination: string;
  }>;
  rideDestinations?: Array<{
    trips: number;
    revenue: number;
    destination: string;
  }>;
}) {
  const displayAirlines =
    airlines?.map((a) => ({
      name: a.airline,
      bookings: a.bookings,
      revenue: `₦${a.revenue.toLocaleString()}`,
    })) || airlinesData;

  const displayFlightDestinations =
    flightDestinations?.map((d) => ({
      name: d.destination,
      trips: d.trips,
      revenue: `₦${d.revenue.toLocaleString()}`,
    })) || destinationsData;

  const displayRideDestinations =
    rideDestinations?.map((d) => ({
      name: d.destination,
      trips: d.trips,
      revenue: `₦${d.revenue.toLocaleString()}`,
    })) || destinationsData; // Fallback to destinationsData if no ride data provided

  return (
    <Box sx={{ mt: 5 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, mb: 3, fontSize: { xs: 20, sm: 24 } }}
      >
        Top Performing Services
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                <img
                  src={planeIcon}
                  alt="plane"
                  style={{ width: 20, height: 20 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 0, sm: 0.5 },
                    fontSize: { xs: 18, sm: 20 },
                  }}
                >
                  Top Airlines
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {displayAirlines.map((airline, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.5,
                      borderBottom:
                        index !== displayAirlines.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#e8f5e9",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <img
                          src={planeIcon}
                          alt="plane"
                          style={{ width: 18, height: 18 }}
                        />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: 14, sm: 16 },
                          }}
                        >
                          {airline.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={{ xs: 12, sm: 14 }}
                        >
                          {airline.bookings} Bookings
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#10b981",
                          mb: 0.5,
                          fontSize: { xs: 14, sm: 16 },
                        }}
                      >
                        {airline.revenue}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize={{ xs: 12, sm: 14 }}
                      >
                        Revenue
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                <img
                  src={locationIcon}
                  alt="location"
                  style={{ width: 20, height: 20 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 0, sm: 0.5 },
                    fontSize: { xs: 18, sm: 20 },
                  }}
                >
                  Top Destinations (Flight)
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {displayFlightDestinations.map((destination, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.5,
                      borderBottom:
                        index !== displayFlightDestinations.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#e3f2fd",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <img
                          src={locationIcon}
                          alt="location"
                          style={{ width: 18, height: 18 }}
                        />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: 14, sm: 16 },
                          }}
                        >
                          {destination.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={{ xs: 12, sm: 14 }}
                        >
                          {destination.trips} Trips
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#10b981",
                          mb: 0.5,
                          fontSize: { xs: 14, sm: 16 },
                        }}
                      >
                        {destination.revenue}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize={{ xs: 12, sm: 14 }}
                      >
                        Revenue
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} mb={5}>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                <img
                  src={carIcon}
                  alt="car"
                  style={{ width: 20, height: 20 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 0, sm: 0.5 },
                    fontSize: { xs: 18, sm: 20 },
                  }}
                >
                  Top Destinations (Ride)
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {displayRideDestinations.map((destination, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.5,
                      borderBottom:
                        index !== displayRideDestinations.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#f3e5f5",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <img
                          src={carIcon}
                          alt="car"
                          style={{ width: 18, height: 18 }}
                        />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: 14, sm: 16 },
                          }}
                        >
                          {destination.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={{ xs: 12, sm: 14 }}
                        >
                          {destination.trips} Trips
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: "#10b981",
                          mb: 0.5,
                          fontSize: { xs: 14, sm: 16 },
                        }}
                      >
                        {destination.revenue}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize={{ xs: 12, sm: 14 }}
                      >
                        Revenue
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
